import { type AddTaskFormTypes } from '@/components/Modals/AddTask/AddTask.model.ts';
import { _notNil } from '@/littledash.ts';
import {
  type TaskOneOffScheduleCreate,
  type TaskRecurringScheduleCreate,
  type TaskSpecCreate,
} from '@/model/Task.model.ts';
import { type UserApiId } from '@/model/User.model.ts';
import { type TeamGlossaryApiId } from '@/model/Glosary.model.ts';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils.ts';

export interface TaskSpecWrapper {
  id: string;
  taskSpec: TaskSpecCreate;
}

export type InvalidUsersIssue = { type: 'invalid-users'; users: Array<UserApiId> };
export type InvalidGroupsIssue = { type: 'invalid-groups'; groups: Array<string> };
export type InvalidSamplesIssue = { type: 'invalid-samples'; samples: Array<TeamGlossaryApiId> };
export type InvalidObservationsIssue = { type: 'invalid-observations'; observations: Array<TeamGlossaryApiId> };
export type InvalidTreatmentsIssue = { type: 'invalid-treatments'; treatments: Array<string> };
export type InvalidMeasurementsIssue = { type: 'invalid-measurements'; measurements: Array<string> };
export type TaskSpecV2Issue =
  | InvalidUsersIssue
  | InvalidGroupsIssue
  | InvalidSamplesIssue
  | InvalidObservationsIssue
  | InvalidMeasurementsIssue
  | InvalidTreatmentsIssue;
export type TaskSpecIssueAction =
  | { type: 'close-modal' }
  | { type: 'add-invalid-users'; taskSpecId: string; users: Array<UserApiId>; access: 'read' | 'write' }
  | { type: 'remove-invalid-users'; taskSpecId: string; users: Array<UserApiId>; allTasks: boolean }
  | { type: 'open-task-spec'; taskSpecId: string; tab?: AddTaskFormTypes }
  | { type: 'remove-invalid-groups-and-open'; taskSpecId: string; groups: Array<string> }
  | { type: 'remove-invalid-samples-and-open'; taskSpecId: string; samples: Array<TeamGlossaryApiId> }
  | { type: 'remove-invalid-observations-and-open'; taskSpecId: string; observations: Array<TeamGlossaryApiId> }
  | { type: 'remove-invalid-measurements-and-open'; taskSpecId: string; measurements: Array<string> }
  | { type: 'remove-invalid-treatments-and-open'; taskSpecId: string; treatments: Array<string> }
  | { type: 'delete-task'; taskSpecId: string };

export const validateTaskSpec = (
  taskSpecs: Array<TaskSpecWrapper>,
  studyUserIds: Set<UserApiId>,
  studyGroupIds: Set<string>,
  studySampleIds: Set<TeamGlossaryApiId>,
  observationIds: Set<TeamGlossaryApiId>,
  treatmentIds: Set<string>,
  measurementIds: Set<string>
): Map<TaskSpecWrapper['id'], Array<TaskSpecV2Issue>> =>
  taskSpecs.reduce((acc, taskSpec) => {
    const invalidGroupsIssue = validateTaskSpecGroups(taskSpec, studyGroupIds);
    if (_notNil(invalidGroupsIssue)) {
      acc.set(taskSpec.id, [...(acc.get(taskSpec.id) ?? []), invalidGroupsIssue]);
    }
    const invalidSamplesIssue = validateTaskSpecSamples(taskSpec, studySampleIds);
    if (_notNil(invalidSamplesIssue)) {
      acc.set(taskSpec.id, [...(acc.get(taskSpec.id) ?? []), invalidSamplesIssue]);
    }
    const invalidObservationsIssue = validateTaskSpecObservations(taskSpec, observationIds);
    if (_notNil(invalidObservationsIssue)) {
      acc.set(taskSpec.id, [...(acc.get(taskSpec.id) ?? []), invalidObservationsIssue]);
    }
    const invalidUsersIssue = validateTaskSpecUsers(taskSpec, studyUserIds);
    if (_notNil(invalidUsersIssue)) {
      acc.set(taskSpec.id, [...(acc.get(taskSpec.id) ?? []), invalidUsersIssue]);
    }
    const invalidMeasurementsIssue = validateTaskSpecMeasurements(taskSpec, measurementIds);
    if (_notNil(invalidMeasurementsIssue)) {
      acc.set(taskSpec.id, [...(acc.get(taskSpec.id) ?? []), invalidMeasurementsIssue]);
    }
    const invalidTreatmentsIssue = validateTaskSpecTreatments(taskSpec, treatmentIds);
    if (_notNil(invalidTreatmentsIssue)) {
      acc.set(taskSpec.id, [...(acc.get(taskSpec.id) ?? []), invalidTreatmentsIssue]);
    }
    return acc;
  }, new Map<TaskSpecWrapper['id'], Array<TaskSpecV2Issue>>());

export const convertTaskSpecScheduleToIsoDate = (
  taskSpecSchedule: TaskRecurringScheduleCreate | TaskOneOffScheduleCreate,
  timezone: string
): TaskRecurringScheduleCreate | TaskOneOffScheduleCreate => {
  if (!DateUtils.isValidDate(taskSpecSchedule.duration.start)) {
    taskSpecSchedule.duration.start = DateUtils.renderDateTime(taskSpecSchedule.duration.start, {
      format: DateRenderFormat.ISODate,
      timezone: timezone ?? undefined,
    });
  }
  if (!DateUtils.isValidDate(taskSpecSchedule.duration.end)) {
    taskSpecSchedule.duration.end = DateUtils.renderDateTime(taskSpecSchedule.duration.end, {
      format: DateRenderFormat.ISODate,
      timezone: timezone ?? undefined,
    });
  }
  taskSpecSchedule.duration.type = 'date';
  return taskSpecSchedule;
};

const validateTaskSpecUsers = (
  taskSpec: TaskSpecWrapper,
  studyUserIds: Set<UserApiId>
): InvalidUsersIssue | undefined => {
  const invalidUsers = (taskSpec.taskSpec.assignees ?? []).reduce<Array<UserApiId>>(
    (acc, userId) => (studyUserIds.has(userId) ? acc : [...acc, userId]),
    []
  );
  if (invalidUsers.length > 0) {
    return { type: 'invalid-users', users: invalidUsers };
  }
};
const validateTaskSpecGroups = (
  taskSpec: TaskSpecWrapper,
  studyGroupIds: Set<string>
): InvalidGroupsIssue | undefined => {
  if (taskSpec.taskSpec.target.type === 'group') {
    const invalidGroups = taskSpec.taskSpec.target.groups.reduce<Array<string>>(
      (acc, groupId) => (studyGroupIds.has(groupId) ? acc : [...acc, groupId]),
      []
    );
    if (invalidGroups.length > 0) {
      return { type: 'invalid-groups', groups: invalidGroups };
    }
  }
};

const validateTaskSpecSamples = (
  taskSpec: TaskSpecWrapper,
  studySampleIds: Set<TeamGlossaryApiId>
): InvalidSamplesIssue | undefined => {
  if (taskSpec.taskSpec.type === 'sample') {
    const invalidSamples = taskSpec.taskSpec.execution?.sample?.samples?.reduce<Array<TeamGlossaryApiId>>(
      (acc, sampleId) => (studySampleIds.has(sampleId) ? acc : [...acc, sampleId]),
      []
    );
    if ((invalidSamples ?? []).length > 0) {
      return { type: 'invalid-samples', samples: invalidSamples ?? [] };
    }
  }
};

const validateTaskSpecMeasurements = (
  taskSpec: TaskSpecWrapper,
  measurementIds: Set<string>
): InvalidMeasurementsIssue | undefined => {
  if (taskSpec.taskSpec.type === 'measurement') {
    const measurements = (taskSpec.taskSpec.execution?.measurement?.measurements ?? []).reduce<Array<string>>(
      (acc, measurementId) => (measurementIds.has(measurementId) ? acc : [...acc, measurementId]),
      []
    );
    if (measurements.length > 0) {
      return { type: 'invalid-measurements', measurements };
    }
  }
};
const validateTaskSpecTreatments = (
  taskSpec: TaskSpecWrapper,
  treatmentIds: Set<string>
): InvalidTreatmentsIssue | undefined => {
  if (taskSpec.taskSpec.type === 'dosing') {
    const treatments = (taskSpec.taskSpec.execution?.dosing?.treatments ?? []).reduce<Array<string>>(
      (acc, treatmentId) => (treatmentIds.has(`${treatmentId}`) ? acc : [...acc, `${treatmentId}`]),
      []
    );
    if (treatments.length > 0) {
      return { type: 'invalid-treatments', treatments };
    }
  }
};

const validateTaskSpecObservations = (
  taskSpec: TaskSpecWrapper,
  observationIds: Set<TeamGlossaryApiId>
): InvalidObservationsIssue | undefined => {
  if (taskSpec.taskSpec.type === 'observation') {
    const invalidObservations = (taskSpec.taskSpec.execution?.observation?.observations ?? []).reduce<
      Array<TeamGlossaryApiId>
    >((acc, observationId) => (observationIds.has(observationId) ? acc : [...acc, observationId]), []);
    if ((invalidObservations ?? []).length > 0) {
      return { type: 'invalid-observations', observations: invalidObservations ?? [] };
    }
  }
};

export type IssueActionOption = { value: string; label: string };

export const issueConfig = {
  'invalid-groups': {
    label: 'Group',
    testComponent: 'InvalidGroupsCard' as const,
    getCount: (issue: TaskSpecV2Issue) => (issue.type === 'invalid-groups' ? issue.groups.length : 0),
    removeAction: { value: 'remove-invalid-groups-and-open', label: 'Remove and open task' },
    buildRemoveAction: (taskSpecId: string, issue: TaskSpecV2Issue): TaskSpecIssueAction | null =>
      issue.type === 'invalid-groups'
        ? { type: 'remove-invalid-groups-and-open', taskSpecId, groups: issue.groups }
        : null,
  },
  'invalid-samples': {
    label: 'Sample',
    testComponent: 'InvalidSamplesCard' as const,
    getCount: (issue: TaskSpecV2Issue) => (issue.type === 'invalid-samples' ? issue.samples.length : 0),
    removeAction: { value: 'remove-invalid-samples-and-open', label: 'Remove and open task' },
    buildRemoveAction: (taskSpecId: string, issue: TaskSpecV2Issue): TaskSpecIssueAction | null =>
      issue.type === 'invalid-samples'
        ? { type: 'remove-invalid-samples-and-open', taskSpecId, samples: issue.samples }
        : null,
  },
  'invalid-observations': {
    label: 'Observation',
    testComponent: 'InvalidObservationsCard' as const,
    getCount: (issue: TaskSpecV2Issue) => (issue.type === 'invalid-observations' ? issue.observations.length : 0),
    removeAction: { value: 'remove-invalid-observations-and-open', label: 'Remove and open task' },
    buildRemoveAction: (taskSpecId: string, issue: TaskSpecV2Issue): TaskSpecIssueAction | null =>
      issue.type === 'invalid-observations'
        ? { type: 'remove-invalid-observations-and-open', taskSpecId, observations: issue.observations }
        : null,
  },
  'invalid-measurements': {
    label: 'Measurement',
    testComponent: 'InvalidMeasurementsCard' as const,
    getCount: (issue: TaskSpecV2Issue) => (issue.type === 'invalid-measurements' ? issue.measurements.length : 0),
    removeAction: { value: 'remove-invalid-measurements-and-open', label: 'Remove and open task' },
    buildRemoveAction: (taskSpecId: string, issue: TaskSpecV2Issue): TaskSpecIssueAction | null =>
      issue.type === 'invalid-measurements'
        ? { type: 'remove-invalid-measurements-and-open', taskSpecId, measurements: issue.measurements }
        : null,
  },
  'invalid-treatments': {
    label: 'Treatment',
    testComponent: 'InvalidTreatmentsCard' as const,
    getCount: (issue: TaskSpecV2Issue) => (issue.type === 'invalid-treatments' ? issue.treatments.length : 0),
    removeAction: { value: 'remove-invalid-treatments-and-open', label: 'Remove and open task' },
    buildRemoveAction: (taskSpecId: string, issue: TaskSpecV2Issue): TaskSpecIssueAction | null =>
      issue.type === 'invalid-treatments'
        ? { type: 'remove-invalid-treatments-and-open', taskSpecId, treatments: issue.treatments }
        : null,
  },
} satisfies Record<
  Exclude<TaskSpecV2Issue['type'], 'invalid-users'>,
  {
    label: string;
    testComponent: Capitalize<string>;
    getCount: (issue: TaskSpecV2Issue) => number;
    removeAction: IssueActionOption;
    buildRemoveAction: (taskSpecId: string, issue: TaskSpecV2Issue) => TaskSpecIssueAction | null;
  }
>;
